import React, { useEffect, useState } from 'react'

import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  MessagePlusSquare,
} from '@/components/Icons'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useDocumentStore } from '@/features/document'
import { useUpdateDocument } from '@/features/document/hooks'
import {
  ReviewerStatus,
  TflStatus,
  UserRole,
} from '@/features/view-tfl-uploaded/types.ts'
import { useCurrentUser } from '@/hooks/useAuth'
import { DocumentAction } from '@/schema'
import { cn } from '@/utils/common'

import { useTflViewerStore } from '../store'
import { getUserRole } from '../utils/validation'

import { ReviewerDetailsDialog } from './ReviewerDetailsDialog'

const SCROLL_THRESHOLD = 50

export interface NoticeBarProps {
  documentId: string
  className?: string
  scrollContainerRef?: React.RefObject<HTMLElement>
}

interface NoticeBarConfig {
  message: React.ReactNode
  bgColor: string
  iconColor: string
  variant: 'success' | 'warning' | 'rejected'
  showApproveButton: boolean
  Icon: React.ComponentType<{ className?: string }>
}

type ResolveNoticeArgs = {
  isAuthorRole: boolean
  isReviewerRole: boolean
  currentReviewer?: { status: ReviewerStatus } | null
  reviewers: Array<{ status: ReviewerStatus }>
}

const resolveNoticeConfig = ({
  isAuthorRole,
  isReviewerRole,
  currentReviewer,
  reviewers,
}: ResolveNoticeArgs): NoticeBarConfig | null => {
  if (isReviewerRole && currentReviewer) {
    return getReviewerNoticeConfig(currentReviewer.status)
  }

  if (isAuthorRole) {
    if (!shouldRenderNoticeBarForAuthor(reviewers)) return null

    const pendingCount = reviewers.filter(
      (r) => r.status === ReviewerStatus.PENDING
    ).length
    const allApproved = reviewers.every(
      (r) => r.status === ReviewerStatus.APPROVED
    )

    return getAuthorNoticeConfig(allApproved, pendingCount)
  }

  return null
}

const getReviewerNoticeConfig = (
  reviewerStatus: ReviewerStatus
): NoticeBarConfig => {
  switch (reviewerStatus) {
    case ReviewerStatus.APPROVED:
      return {
        message: 'You approved this file',
        bgColor: 'bg-success-50',
        iconColor: 'text-foreground-fg-success-primary',
        variant: 'success',
        showApproveButton: false,
        Icon: CheckCircle,
      }
    case ReviewerStatus.REJECTED:
      return {
        message: "You've added comments to this file",
        bgColor: 'bg-tertiary',
        iconColor: 'text-foreground-fg-secondary_hover',
        variant: 'rejected',
        showApproveButton: false,
        Icon: MessagePlusSquare,
      }
    case ReviewerStatus.PENDING:
    default:
      return {
        message: 'Pending Approval',
        bgColor: 'bg-warning-primary',
        iconColor: 'text-foreground-fg-warning-primary',
        variant: 'warning',
        showApproveButton: true,
        Icon: AlertCircle,
      }
  }
}

const getAuthorNoticeConfig = (
  isAllApproved: boolean,
  pendingCount: number
): NoticeBarConfig => {
  if (isAllApproved) {
    return {
      message: 'All reviewers approved',
      bgColor: 'bg-success-50',
      iconColor: 'text-foreground-fg-success-primary',
      variant: 'success',
      showApproveButton: false,
      Icon: CheckCircle,
    }
  }

  const reviewerText = pendingCount === 1 ? 'reviewer' : 'reviewers'
  return {
    message: `Waiting for ${pendingCount} ${reviewerText} to approve.`,
    bgColor: 'bg-warning-primary',
    iconColor: 'text-foreground-fg-warning-primary',
    variant: 'warning',
    showApproveButton: false,
    Icon: AlertCircle,
  }
}

const shouldRenderNoticeBarForAuthor = (
  reviewers: Array<{ status: ReviewerStatus }>
): boolean => {
  if (reviewers.length === 0) return false
  const hasPending = reviewers.some((r) => r.status === ReviewerStatus.PENDING)
  const allApproved = reviewers.every(
    (r) => r.status === ReviewerStatus.APPROVED
  )
  return hasPending || allApproved
}

export const NoticeBar: React.FC<NoticeBarProps> = ({
  documentId,
  className,
  scrollContainerRef,
}) => {
  const viewDetailsButtonRef = React.useRef<HTMLButtonElement>(null)
  const lastScrollY = React.useRef(0)
  const scrollTimeoutRef = React.useRef<NodeJS.Timeout>()
  const [isVisible, setIsVisible] = React.useState(true)

  const { data: currentUser } = useCurrentUser()
  const { getIsLatestVersion } = useDocumentStore()
  const {
    setReviewerDetailsDialogOpen,
    reviewerDetailsDialogOpen,
    metadataByDocId,
  } = useTflViewerStore()
  const updateDocumentMutation = useUpdateDocument()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const metadata = metadataByDocId[documentId]
  const isLatestVersion = getIsLatestVersion(documentId)
  const userRole = getUserRole(
    metadata?.owner,
    metadata?.created_by,
    metadata?.reviewers,
    currentUser?.id
  )

  const isAuthorRole =
    userRole === UserRole.AUTHOR || userRole === UserRole.OWNER_AUTHOR
  const isReviewerRole =
    userRole === UserRole.REVIEWER || userRole === UserRole.AUTHOR_REVIEWER
  const currentReviewer = metadata?.reviewers?.find(
    (reviewer) => reviewer.id === currentUser?.id
  )

  const noticeConfig = React.useMemo(() => {
    return resolveNoticeConfig({
      isAuthorRole,
      isReviewerRole,
      currentReviewer: currentReviewer ?? null,
      reviewers: metadata?.reviewers ?? [],
    })
  }, [isAuthorRole, isReviewerRole, currentReviewer, metadata?.reviewers])

  useEffect(() => {
    const scrollContainer = scrollContainerRef?.current
    if (!scrollContainer) return

    const handleScroll = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }

      const currentScrollY = scrollContainer.scrollTop

      if (
        currentScrollY > lastScrollY.current &&
        currentScrollY > SCROLL_THRESHOLD
      ) {
        if (reviewerDetailsDialogOpen) {
          setReviewerDetailsDialogOpen(false)
        }

        scrollTimeoutRef.current = setTimeout(() => {
          setIsVisible(false)
        }, 50)
      } else if (
        currentScrollY < lastScrollY.current &&
        currentScrollY < SCROLL_THRESHOLD
      ) {
        scrollTimeoutRef.current = setTimeout(() => {
          setIsVisible(true)
        }, 50)
      }

      lastScrollY.current = currentScrollY
    }

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll)
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [
    reviewerDetailsDialogOpen,
    setReviewerDetailsDialogOpen,
    scrollContainerRef,
  ])

  const handleViewDetails = () => {
    setReviewerDetailsDialogOpen(!reviewerDetailsDialogOpen)
  }

  const handleApproveDocument = () => {
    if (!metadata) return
    setIsLoading(true)
    updateDocumentMutation.mutate(
      {
        documentId,
        data: {
          action: DocumentAction.APPROVED,
          deliverable_set_id: metadata.dsId || '',
          display_name: metadata.displayName || '',
          document_type: metadata.kind as unknown as TflStatus,
        },
      },
      {
        onError: () => {
          setIsLoading(false)
        },
      }
    )
  }

  const shouldHideNoticeBar =
    !isLatestVersion ||
    userRole === UserRole.VIEWER ||
    !noticeConfig ||
    metadata?.status === TflStatus.NEW

  if (shouldHideNoticeBar) {
    return null
  }

  return (
    <div
      className={cn(
        'border-b border-blue-200 px-8 flex items-center text-sm gap-2 overflow-hidden',
        'transition-all duration-300 ease-in-out relative',
        noticeConfig.bgColor,
        className
      )}
      style={{
        maxHeight: isVisible ? '100px' : '0px',
        paddingTop: isVisible ? '1rem' : '0px',
        paddingBottom: isVisible ? '1rem' : '0px',
        opacity: isVisible ? 1 : 0,
        zIndex: 10,
      }}
    >
      <div className="flex items-center gap-x-sm">
        <noticeConfig.Icon className={cn('w-5 h-5', noticeConfig.iconColor)} />
        <span className="text-secondary-700 font-medium">
          {noticeConfig.message}
        </span>
        {isAuthorRole && (
          <div className="relative">
            <Button
              ref={viewDetailsButtonRef}
              variant="link"
              onClick={handleViewDetails}
              className={cn(
                'underline font-medium',
                noticeConfig.variant === 'success'
                  ? 'text-success-600'
                  : 'text-warning-600'
              )}
              trailingIcon={
                reviewerDetailsDialogOpen ? (
                  <ChevronUp
                    className={cn('w-4 h-4', noticeConfig.iconColor)}
                  />
                ) : (
                  <ChevronDown
                    className={cn('w-4 h-4', noticeConfig.iconColor)}
                  />
                )
              }
            >
              View details
            </Button>
            <ReviewerDetailsDialog
              open={reviewerDetailsDialogOpen}
              onClose={() => setReviewerDetailsDialogOpen(false)}
              documentId={documentId}
              anchorEl={viewDetailsButtonRef.current}
            />
          </div>
        )}
      </div>
      {noticeConfig.showApproveButton && (
        <div className="flex items-center gap-2 ml-auto">
          <Button
            variant="text"
            className="px-3 py-1.5 text-sm bg-transparent hover:bg-transparent text-warning-primary-600 disabled:opacity-50"
            onClick={handleApproveDocument}
            disabled={isLoading}
            leadingIcon={
              isLoading ? (
                <Spinner className="text-warning-primary-600" />
              ) : undefined
            }
          >
            {isLoading ? '' : 'Approve'}
          </Button>
        </div>
      )}
    </div>
  )
}
